// eslint-disable-next-line no-undef
class TableOfContentsObserver extends HTMLElement {
  constructor () {
    super()
    this.toc = null
    this.footer = null
    this.header = null
    this.observer = null
    this.headerHeight = 0
    this.baseHeaderHeight = 0
    this.bestHeadersHeight = 0
    this.footerHeight = 0
    this.tocNaturalHeight = 0
    this.bestHeadersObserver = null
    this.positioningComplete = false
    this.init()
  }

  init () {
    document.addEventListener('DOMContentLoaded', () => {
      this.toc = document.querySelector('.toc')
      this.footer = document.querySelector('.sleepdoctor-footer, .footer, footer')
      this.header = document.querySelector('sticky-header, .header, header')

      if (!this.toc || !this.footer) return

      // Hide TOC initially until positioning is calculated
      this.toc.style.visibility = 'hidden'

      this.calculateHeights()

      // For best-product pages, don't call setupPositioning yet
      // Let the observer set the initial position
      const sdBestContainer = document.querySelector('.sd-best')
      if (!sdBestContainer) {
        this.setupPositioning()
      }

      this.setupObserver()
      this.setupBestHeadersObserver()
      this.setupScrollListener()

      // Wait for initial positioning to complete
      this.waitForPositioning()

      window.addEventListener('resize', () => {
        this.calculateHeights()
        this.setupPositioning()
      })
    })
  }

  calculateHeights () {
    this.baseHeaderHeight = 0

    if (this.header) {
      this.baseHeaderHeight += this.header.offsetHeight
    }

    const announcementBar = document.querySelector('.sleepdoctor-announcement-bar')
    if (announcementBar) {
      this.baseHeaderHeight += announcementBar.offsetHeight
    }

    this.baseHeaderHeight += 20

    this.bestHeadersHeight = 0
    const sdBestContainer = document.querySelector('.sd-best')
    const sdBestIndividualContainer = document.querySelector('.sd-best-individual')

    // Only calculate best headers height for actual best-product pages
    if (sdBestContainer) {
      const bestHeader = sdBestContainer.querySelector('.sd-best-header')
      if (bestHeader) {
        this.bestHeadersHeight += bestHeader.offsetHeight
      }

      const bestAffText = sdBestContainer.querySelector('.sd-best-aff-text')
      if (bestAffText) {
        this.bestHeadersHeight += bestAffText.offsetHeight
      }
    } else if (sdBestIndividualContainer) {
      // For IMR pages, include the affiliate text in the best headers height
      const bestAffText = sdBestIndividualContainer.querySelector('.sd-best-aff-text')
      if (bestAffText) {
        this.bestHeadersHeight += bestAffText.offsetHeight
      }
    }

    this.headerHeight = this.baseHeaderHeight + this.bestHeadersHeight

    if (this.footer) {
      this.footerHeight = this.footer.offsetHeight
    }

    if (this.toc && this.tocNaturalHeight === 0) {
      const originalMaxHeight = this.toc.style.maxHeight
      const originalHeight = this.toc.style.height
      this.toc.style.maxHeight = ''
      this.toc.style.height = ''
      this.tocNaturalHeight = this.toc.scrollHeight
      this.toc.style.maxHeight = originalMaxHeight
      this.toc.style.height = originalHeight
    }
  }

  setupPositioning () {
    // Check if best headers are actually visible in viewport
    const sdBestContainer = document.querySelector('.sd-best')
    const sdBestIndividualContainer = document.querySelector('.sd-best-individual')

    let bestElementsInViewport = false

    if (sdBestContainer) {
      const bestHeader = sdBestContainer.querySelector('.sd-best-header')
      const bestAffText = sdBestContainer.querySelector('.sd-best-aff-text')
      const lastElement = bestAffText || bestHeader

      if (lastElement) {
        const rect = lastElement.getBoundingClientRect()
        bestElementsInViewport = rect.bottom > 0
      }
    } else if (sdBestIndividualContainer) {
      const bestAffText = sdBestIndividualContainer.querySelector('.sd-best-aff-text')

      if (bestAffText) {
        const rect = bestAffText.getBoundingClientRect()
        bestElementsInViewport = rect.bottom > 0
      }
    }

    // Use full header height if best elements are in viewport, otherwise just base height
    const topPosition = bestElementsInViewport ? this.headerHeight : this.baseHeaderHeight
    this.toc.style.top = `${topPosition}px`

    const containerWidth = 1200
    const tocWidth = 280
    const padding = 40
    const leftShift = 120

    if (window.innerWidth >= containerWidth + tocWidth + (padding * 2)) {
      const rightPosition = (window.innerWidth - containerWidth) / 2 - tocWidth - padding + leftShift
      this.toc.style.right = `${Math.max(rightPosition, 20)}px`
    } else {
      this.toc.style.right = '20px'
    }
  }

  setupObserver () {
    const thresholds = []
    for (let i = 0; i <= 1.0; i += 0.01) {
      thresholds.push(i)
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: thresholds
    }

    // eslint-disable-next-line no-undef
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const footerRect = entry.boundingClientRect
          const visibleFooterHeight = Math.max(0, window.innerHeight - footerRect.top)

          this.toc.classList.add('toc--bottom')
          this.toc.style.position = 'fixed'
          this.toc.style.top = `${this.baseHeaderHeight}px`

          const footerFullyVisible = footerRect.top <= window.innerHeight && footerRect.bottom >= 0 &&
                                     footerRect.top >= 0 && visibleFooterHeight >= this.footerHeight
          const additionalOffset = footerFullyVisible ? 20 : 0
          const availableHeight = window.innerHeight - this.baseHeaderHeight - visibleFooterHeight - 40 - additionalOffset

          if (this.tocNaturalHeight > availableHeight) {
            this.toc.style.maxHeight = `${Math.max(200, availableHeight)}px`
            this.toc.style.overflowY = 'auto'
          } else {
            this.toc.style.maxHeight = `${this.tocNaturalHeight}px`
            this.toc.style.overflowY = ''
          }
        } else {
          this.toc.classList.remove('toc--bottom')
          this.toc.style.position = 'fixed'
          this.toc.style.top = `${this.baseHeaderHeight}px`
          this.toc.style.maxHeight = ''
          this.toc.style.overflowY = ''
        }
      })
    }, options)

    this.observer.observe(this.footer)
  }

  setupBestHeadersObserver () {
    if (this.bestHeadersHeight === 0) {
      // No best headers, mark positioning as complete
      this.positioningComplete = true
      return
    }

    const sdBestContainer = document.querySelector('.sd-best')
    const sdBestIndividualContainer = document.querySelector('.sd-best-individual')

    let lastBestElement = null

    if (sdBestContainer) {
      lastBestElement = sdBestContainer.querySelector('.sd-best-aff-text') || sdBestContainer.querySelector('.sd-best-header')

      // For best-product pages, set initial position immediately
      // Check if best headers are in viewport
      if (lastBestElement) {
        const rect = lastBestElement.getBoundingClientRect()
        const initialTop = rect.bottom > 0 ? this.headerHeight : this.baseHeaderHeight
        this.toc.style.top = `${initialTop}px`

        // Also set right position for best-product pages
        this.setupPositioning()
      }
    } else if (sdBestIndividualContainer) {
      lastBestElement = sdBestIndividualContainer.querySelector('.sd-best-aff-text')
    }

    if (!lastBestElement) {
      // No element to observe, mark positioning as complete
      this.positioningComplete = true
      return
    }

    const options = {
      root: null,
      rootMargin: `-${this.baseHeaderHeight}px 0px 0px 0px`,
      threshold: 0
    }

    // eslint-disable-next-line no-undef
    this.bestHeadersObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Update position first
        if (!this.toc.classList.contains('toc--bottom')) {
          if (!entry.isIntersecting) {
            this.toc.style.top = `${this.baseHeaderHeight}px`
          } else {
            this.toc.style.top = `${this.headerHeight}px`
          }
        }

        // Then mark positioning as complete after first observation
        if (!this.positioningComplete) {
          this.positioningComplete = true
          this.showTocIfReady()
        }
      })
    }, options)

    this.bestHeadersObserver.observe(lastBestElement)
  }

  setupScrollListener () {
    let ticking = false

    const updateTocHeight = () => {
      const footerRect = this.footer.getBoundingClientRect()

      if (footerRect.top < window.innerHeight) {
        const visibleFooterHeight = Math.max(0, window.innerHeight - footerRect.top)

        const footerFullyVisible = footerRect.top <= window.innerHeight && footerRect.bottom >= 0 &&
                                   footerRect.top >= 0 && visibleFooterHeight >= this.footerHeight
        const additionalOffset = footerFullyVisible ? 20 : 0
        const availableHeight = window.innerHeight - this.baseHeaderHeight - visibleFooterHeight - 40 - additionalOffset

        if (this.tocNaturalHeight > availableHeight) {
          this.toc.style.maxHeight = `${Math.max(200, availableHeight)}px`
          this.toc.style.overflowY = 'auto'
        } else {
          this.toc.style.maxHeight = `${this.tocNaturalHeight}px`
          this.toc.style.overflowY = ''
        }
      } else {
        this.toc.style.maxHeight = ''
        this.toc.style.overflowY = ''
      }

      ticking = false
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateTocHeight)
        ticking = true
      }
    })
  }

  waitForPositioning () {
    // If no best headers, show immediately
    if (this.bestHeadersHeight === 0) {
      this.showTocIfReady()
      return
    }

    // Best-product pages have more complex headers, give them more time
    const sdBestContainer = document.querySelector('.sd-best')
    const timeout = sdBestContainer ? 750 : 500

    // Wait for observers to fire
    setTimeout(() => {
      if (!this.positioningComplete) {
        this.positioningComplete = true
        this.showTocIfReady()
      }
    }, timeout)
  }

  showTocIfReady () {
    if (this.positioningComplete && this.toc) {
      this.toc.style.visibility = 'visible'
      this.toc.style.opacity = '1'
    }
  }

  disconnectedCallback () {
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.bestHeadersObserver) {
      this.bestHeadersObserver.disconnect()
    }
  }
}

// eslint-disable-next-line no-undef
customElements.define('table-of-contents-observer', TableOfContentsObserver)
